/**
 * ウィジェットの大きさを 4x4 のマス目から選ぶ。
 *
 * 左上を起点にした矩形で表すので、押したマスがそのまま行×列になる。
 */

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { cn } from '@/lib/utils'
import { GRID_SPANS, parseWidgetSize, toWidgetSize, type WidgetSize } from '@/store/useHomeLayoutStore'

type WidgetSizePickerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  label: string
  size: WidgetSize
  onSelect: (size: WidgetSize) => void
}

const CELLS = GRID_SPANS.flatMap((rows) => GRID_SPANS.map((cols) => ({ rows, cols })))

export const WidgetSizePicker = ({ open, onOpenChange, label, size, onSelect }: WidgetSizePickerProps) => {
  const current = parseWidgetSize(size)

  return (
    <BottomSheet title={`${label} の大きさ`} open={open} onOpenChange={onOpenChange} snapPoints={[0.5]} keepMounted>
      <div className="flex flex-col items-center gap-4 px-4 pb-6">
        <div role="radiogroup" aria-label={`${label} の大きさ`} className="grid w-56 grid-cols-4 gap-1.5">
          {CELLS.map(({ rows, cols }) => {
            const value = toWidgetSize(rows, cols)
            // 左上からの矩形を塗って、選んだマスがそのまま行×列になることを示す
            const filled = rows <= current.rows && cols <= current.cols
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={value === size}
                aria-label={`${rows} 行 × ${cols} 列`}
                onClick={() => {
                  onSelect(value)
                  onOpenChange(false)
                }}
                className={cn(
                  'aspect-square rounded-md border p-0 active:opacity-70',
                  filled ? 'border-sky-400 bg-sky-500/30' : 'border-border/60 bg-muted',
                )}
              />
            )
          })}
        </div>
        <p className="text-xs text-muted-foreground tabular-nums">
          現在 {current.rows} 行 × {current.cols} 列
        </p>
      </div>
    </BottomSheet>
  )
}
